/**
 * <scgb-search mode="modal|results"> — the only custom element this package
 * exposes. Two modes:
 *
 *   modal:   compact, autocomplete-style dropdown rendered inside the
 *            existing Bootstrap modal in skiclub-theme/template-parts/
 *            modal/search.php.
 *   results: full-page replacement for skiclub-theme/search.php's body —
 *            input, tab strip, paginated result list.
 *
 * The element owns:
 *   - query input & debounce
 *   - URL state (?s=, ?tab=)
 *   - keyboard nav (ARIA combobox pattern)
 *   - Meili query
 *   - result rendering (per-post-type cards)
 *
 * It does NOT own:
 *   - opening/closing the Bootstrap modal — Bootstrap keeps doing that.
 *   - the page banner / footer in results mode — those stay server-rendered.
 */

import { LitElement, html, css, unsafeCSS, type TemplateResult, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";

import { search, type SearchResults } from "./lib/meili.js";
import { TAB_MAP, POST_TYPE_LABEL, SCOPE_LABEL, type PostType, type SearchDoc } from "./lib/schema.js";
import { readUrl, writeUrl, debounce } from "./lib/url-state.js";
import { nextIndex, type ListKey } from "./lib/a11y.js";
import { trackResultClick } from "./lib/analytics.js";
import { loadConfig } from "./lib/config.js";
import {
  availabilityLabel,
  holidaySearch,
  priceLabel,
  withoutChip,
  type Chip,
  type DepartureHit,
  type HolidayResult,
  type ParsedQuery,
} from "./lib/departures.js";
import { boostHolidays, hasHolidayIntent } from "./lib/holidayBoost.js";

import elementStyles from "./styles/element.scss?inline";

type Mode = "modal" | "results";

const DEBOUNCE_MS = 120;
const MODAL_LIMIT = 8;
const RESULTS_LIMIT = 20;
const DEPARTURES_LIMIT = 6;
/** How many holiday pages a holiday-intent search pulls to the top. */
const HOLIDAY_BOOST_LIMIT = 6;

export class ScgbSearchElement extends LitElement {
  static styles = css`${unsafeCSS(elementStyles)}`;

  @property({ type: String }) mode: Mode = "modal";
  @property({ type: String, attribute: "initial-query" }) initialQuery = "";
  @property({ type: String, attribute: "initial-tab" }) initialTab = "all";
  @property({ type: String, attribute: "brand-color" }) brandColor = "#007AC2";
  @property({ type: String, attribute: "initial-popular" }) initialPopular = "";

  // Embed-locked scope. `scope` is a TAB_MAP key (e.g. "resorts"): when set, the
  // element filters every query to that post-type set and hides the tab strip,
  // so a "search for a resort" box can never escape its context. scope-country /
  // scope-ability lock the matching facet (e.g. resorts in Austria).
  @property({ type: String, attribute: "scope" }) scope = "";
  @property({ type: String, attribute: "scope-country" }) scopeCountry = "";
  @property({ type: String, attribute: "scope-ability" }) scopeAbility = "";

  @state() private query = "";
  @state() private tab = "all";
  @state() private results: SearchResults | null = null;
  @state() private loading = false;
  @state() private error: string | null = null;
  @state() private activeIndex = -1;
  @state() private popularSearches: string[] = [];
  /** Plain-English holiday search (proxy /holidays/search); null = none/hidden. */
  @state() private departures: HolidayResult | null = null;
  private departuresSeq = 0;
  /** Main-search sequence, so a slow response never overwrites a newer one. */
  private searchSeq = 0;
  private boostedSeq = -1;

  private runSearch = debounce(() => void this._search(), DEBOUNCE_MS);

  /** True when `scope` names a valid, non-"all" TAB_MAP key — the element is
   *  then locked to that post-type set and the tab strip is suppressed. */
  private get _scoped(): boolean {
    return this.scope !== "" && this.scope !== "all" && this.scope in TAB_MAP;
  }

  private get _placeholder(): string {
    const label = this._scoped ? SCOPE_LABEL[this.scope] : undefined;
    if (label) return `Search ${label}…`;
    return this.mode === "modal" ? "Start typing — resorts, holidays & more" : "Start typing";
  }

  connectedCallback(): void {
    super.connectedCallback();
    const url = readUrl({ query: this.initialQuery, tab: this.initialTab });
    this.query = url.query;
    this.tab = url.tab;
    this.popularSearches = this._parsePopular(this.initialPopular);
    if (this.mode === "results" || this.query) {
      this.runSearch();
    }
  }

  private _parsePopular(raw: string): string[] {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .map((p: unknown) =>
            typeof p === "string" ? p : (p as { query?: string }).query ?? ""
          )
          .filter((s) => s.length > 0)
          .slice(0, 5);
      }
    } catch {
      /* fall through */
    }
    return [];
  }

  private async _search(): Promise<void> {
    const q = this.query.trim();
    if (this.mode === "modal" && q.length < 2) {
      this.results = null;
      this.activeIndex = -1;
      return;
    }
    const seq = ++this.searchSeq;
    this.loading = true;
    this.error = null;
    try {
      const postTypes = this._scoped ? TAB_MAP[this.scope] : (TAB_MAP[this.tab] ?? null);
      const results = await search({
        query: q,
        postTypes,
        country: this.scopeCountry || undefined,
        skiAbility: this.scopeAbility || undefined,
        limit: this.mode === "modal" ? MODAL_LIMIT : RESULTS_LIMIT,
      });
      if (seq !== this.searchSeq) return; // a newer search has started
      this.results = results;
      this.activeIndex = -1;
    } catch (e) {
      if (seq !== this.searchSeq) return;
      this.error = (e as Error).message;
      this.results = null;
    } finally {
      if (seq === this.searchSeq) this.loading = false;
    }
    if (this._wantsBoost() && hasHolidayIntent(q)) void this._boostHolidays(q, seq);
    if (this._wantsDepartures(q)) void this._searchDepartures(q, undefined, seq);
    else this.departures = null;
  }

  /** Holiday pages go first only on the full results page's "all" tab; the
   *  holidays tab already shows nothing else. */
  private _wantsBoost(): boolean {
    return this.mode === "results" && !this._scoped && this.tab === "all";
  }

  /** Reorder the main list so holiday pages lead (see lib/holidayBoost.ts).
   *  Runs at most once per search, and never over a newer search's results. */
  private async _boostHolidays(q: string, seq: number): Promise<void> {
    if (this.boostedSeq === seq) return;
    this.boostedSeq = seq;
    try {
      const holidays = await search({ query: q, postTypes: ["holiday"], limit: HOLIDAY_BOOST_LIMIT });
      if (seq !== this.searchSeq || !this.results) return;
      this.results = { ...this.results, hits: boostHolidays(this.results.hits, holidays.hits) };
    } catch {
      /* keep the normal order */
    }
  }

  /** The departures panel belongs on the full results page, on the all and
   *  holidays tabs (or a holidays-scoped box). Never in the modal dropdown. */
  private _wantsDepartures(q: string): boolean {
    if (this.mode !== "results" || q.length < 2) return false;
    return this._scoped ? this.scope === "holidays" : this.tab === "all" || this.tab === "holidays";
  }

  /** `searchSeq` is passed only for the search's own first parse (not a chip
   *  removal); a date, price or trip-type filter then puts holidays first. */
  private async _searchDepartures(q: string, parsed?: ParsedQuery, searchSeq?: number): Promise<void> {
    const seq = ++this.departuresSeq;
    let host: string;
    try {
      host = loadConfig().host;
    } catch {
      return;
    }
    const r = await holidaySearch(host, { q, parsed, limit: DEPARTURES_LIMIT });
    if (seq !== this.departuresSeq) return; // drop stale responses
    this.departures = r;
    if (searchSeq !== undefined && r && this._wantsBoost() && hasHolidayIntent(q, r.applied)) {
      void this._boostHolidays(q, searchSeq);
    }
  }

  private _removeChip(chip: Chip): void {
    if (!this.departures) return;
    void this._searchDepartures(this.query.trim(), withoutChip(this.departures.parsed, chip));
  }

  private _onInput(e: Event): void {
    const input = e.target as HTMLInputElement;
    this.query = input.value;
    writeUrl({ query: this.query, tab: this.tab });
    this.runSearch();
  }

  private _onKeydown(e: KeyboardEvent): void {
    const hits = this.results?.hits ?? [];
    if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Home" || e.key === "End") {
      e.preventDefault();
      this.activeIndex = nextIndex(this.activeIndex, hits.length, e.key as ListKey);
    } else if (e.key === "Enter" && this.activeIndex >= 0 && hits[this.activeIndex]) {
      e.preventDefault();
      this._trackClick(hits[this.activeIndex], this.activeIndex);
      window.location.href = hits[this.activeIndex].url;
    } else if (e.key === "Enter") {
      // No active result → behave like the legacy form submit: go to the
      // results page for the typed query.
      e.preventDefault();
      this._submit();
    } else if (e.key === "Escape") {
      this.results = null;
      this.activeIndex = -1;
    }
  }

  /** Navigate to the results page for the current query (legacy form-submit
   *  parity; also the box's Search button). In results mode it just re-runs. */
  private _submit(): void {
    const q = this.query.trim();
    if (!q) return;
    if (this.mode === "results") {
      void this._search();
      return;
    }
    window.location.href = `/?s=${encodeURIComponent(q)}`;
  }

  private _selectTab(tab: string): void {
    this.tab = tab;
    writeUrl({ query: this.query, tab: this.tab });
    void this._search();
  }

  /**
   * Beacon the click so we can attribute "what was clicked" to "what was typed"
   * (type "ze" → click Zermatt). Fired before navigation; sendBeacon survives
   * the page unload, so the link's default navigation is left untouched.
   */
  private _trackClick(hit: SearchDoc, index: number): void {
    trackResultClick({
      type: "result_click",
      q: this.query.trim(),
      clickedId: hit.id,
      clickedUrl: hit.url,
      pos: index + 1,
      tab: this.tab,
      mode: this.mode,
      total: this.results?.estimatedTotalHits ?? 0,
    });
  }

  render(): TemplateResult {
    return this.mode === "modal" ? this._renderModal() : this._renderResults();
  }

  /** The search box: left icon, transparent input, primary Search button.
   *  Mirrors the legacy theme `.search-bar` so it matches the live styling;
   *  colours come from the page's inherited --sk-* tokens. parts (box/input/
   *  submit) let the host theme fine-tune per context. */
  private _renderInputBox(): TemplateResult {
    return html`
      <div class="scgb-box" part="box">
        <span class="scgb-box__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="7"></circle>
            <path d="m21 21-4.3-4.3"></path>
          </svg>
        </span>
        <label class="visually-hidden" for="scgb-q">Search</label>
        <input
          id="scgb-q"
          class="scgb-search-input"
          part="input"
          type="search"
          autocomplete="off"
          .value=${this.query}
          @input=${this._onInput}
          @keydown=${this._onKeydown}
          placeholder=${this._placeholder}
          aria-controls="scgb-list"
          aria-activedescendant=${this.activeIndex >= 0 ? `scgb-r-${this.activeIndex}` : ""}
        />
      </div>
    `;
  }

  /** "See all results" link shown under the modal dropdown — replaces the
   *  submit button as the discoverable path to the full results page. */
  private _renderSeeAll(): TemplateResult | typeof nothing {
    const q = this.query.trim();
    const total = this.results?.estimatedTotalHits ?? 0;
    if (!q || total === 0) return nothing;
    return html`
      <a class="scgb-see-all" href="/?s=${encodeURIComponent(q)}">
        See all ${total} result${total === 1 ? "" : "s"}
        <span aria-hidden="true">→</span>
      </a>
    `;
  }

  private _renderModal(): TemplateResult {
    return html`
      <div role="combobox" aria-haspopup="listbox" aria-expanded=${!!this.results}>
        ${this._renderInputBox()}
        ${this._renderListbox()}
        ${this.query ? nothing : this._renderPopular()}
      </div>
    `;
  }

  private _renderResults(): TemplateResult {
    const hits = this.results?.hits ?? [];
    const total = this.results?.estimatedTotalHits ?? 0;
    return html`
      <div role="search">
        ${this._renderInputBox()}
        <p aria-live="polite" class="scgb-result__meta" style="margin-top: 8px;">
          ${this.loading
            ? "Searching…"
            : this.query
              ? `We've found ${total} result${total === 1 ? "" : "s"} for "${this.query}"`
              : "Type a query above"}
        </p>
        ${this._renderTabs()}
        ${this._renderDepartures()}
        ${hits.length === 0 && this.query && !this.loading
          ? html`<div class="scgb-empty">
              <p>No results. Try broadening your query or showing all post types.</p>
            </div>`
          : html`<div class="scgb-results" role="list">
              ${hits.map((h, i) => this._renderResultCard(h, i))}
            </div>`}
      </div>
    `;
  }

  /** "Matching departures": shown when the query parsed into filters (chips),
   *  or on the holidays tab whenever there are bookable departures. */
  private _renderDepartures(): TemplateResult | typeof nothing {
    const d = this.departures;
    if (!d) return nothing;
    const holidaysContext = this._scoped ? this.scope === "holidays" : this.tab === "holidays";
    if (d.applied.length === 0 && !holidaysContext) return nothing;
    if (d.hits.length === 0 && d.applied.length === 0) return nothing;
    return html`
      <section class="scgb-deps" aria-label="Matching departures">
        <h2 class="scgb-deps__title">Matching departures</h2>
        ${d.applied.length
          ? html`<ul class="scgb-deps__chips" aria-label="Applied filters">
              ${d.applied.map(
                (c) => html`<li>
                  <button class="scgb-chip" type="button" @click=${() => this._removeChip(c)} aria-label="Remove filter ${c.label}">
                    ${c.label}<span aria-hidden="true">×</span>
                  </button>
                </li>`,
              )}
            </ul>`
          : nothing}
        ${d.hits.length
          ? html`<div class="scgb-deps__list" role="list">
              ${d.hits.map((h, i) => this._renderDeparture(h, i))}
            </div>`
          : html`<p class="scgb-deps__none">No bookable departures match. Try removing a filter.</p>`}
      </section>
    `;
  }

  private _renderDeparture(hit: DepartureHit, index: number): TemplateResult {
    const price = priceLabel(hit);
    const avail = availabilityLabel(hit);
    return html`
      <a
        class="scgb-dep"
        role="listitem"
        href=${hit.url}
        @click=${() =>
          trackResultClick({
            type: "result_click",
            q: this.query.trim(),
            clickedId: hit.id,
            clickedUrl: hit.url,
            pos: index + 1,
            tab: "departures",
            mode: this.mode,
            total: this.departures?.estimatedTotalHits ?? 0,
          })}
      >
        <div class="scgb-dep__date">${hit.date_label ?? ""}</div>
        <div class="scgb-dep__title">${hit.title}</div>
        <div class="scgb-dep__meta">
          ${[hit.nights ? `${hit.nights} nights` : "", price, avail].filter(Boolean).join(" · ")}
        </div>
      </a>
    `;
  }

  private _renderTabs(): TemplateResult | typeof nothing {
    if (this._scoped) return nothing;
    const tabs = [
      ["all", "Show all"],
      ["pages", "Pages"],
      ["resorts", "Resorts"],
      ["holidays", "Holidays"],
      ["events", "Events"],
      ["ski-tests", "Ski Tests"],
      ["news", "News"],
      ["discounts", "Discounts"],
      ["products", "Products"],
    ] as const;
    return html`
      <ul class="scgb-tabs" role="tablist">
        ${tabs.map(
          ([slug, label]) => html`
            <li>
              <button
                class="scgb-tab"
                role="tab"
                aria-pressed=${this.tab === slug ? "true" : "false"}
                @click=${() => this._selectTab(slug)}
              >
                ${label}
              </button>
            </li>
          `
        )}
      </ul>
    `;
  }

  private _renderListbox(): TemplateResult | typeof nothing {
    const hits = this.results?.hits ?? [];
    if (!this.query || (!this.loading && hits.length === 0)) {
      return this.error
        ? html`<p class="scgb-empty">${this.error}</p>`
        : !this.query
          ? nothing
          : html`<p class="scgb-empty">No results yet.</p>`;
    }
    return html`
      <div
        class="scgb-results"
        role="listbox"
        id="scgb-list"
        aria-label="Search results"
      >
        ${hits.map((h, i) => this._renderResultCard(h, i))}
      </div>
      ${this._renderSeeAll()}
    `;
  }

  private _renderPopular(): TemplateResult | typeof nothing {
    if (this.popularSearches.length === 0) return nothing;
    return html`
      <div class="scgb-popular">
        <div class="scgb-popular__label">Popular searches</div>
        <ul>
          ${this.popularSearches.map(
            (s) => html`
              <li>
                <a href="/?s=${encodeURIComponent(s)}">${s}</a>
              </li>
            `
          )}
        </ul>
      </div>
    `;
  }

  private _renderResultCard(hit: SearchDoc, index: number): TemplateResult {
    const formatted = (hit as unknown as { _formatted?: SearchDoc })._formatted ?? hit;
    const titleHtml = formatted.title ?? hit.title;
    const excerptHtml = formatted.excerpt ?? hit.excerpt;
    const label = POST_TYPE_LABEL[hit.post_type as PostType] ?? hit.post_type;
    return html`
      <a
        class="scgb-result"
        href=${hit.url}
        role="option"
        id="scgb-r-${index}"
        aria-selected=${this.activeIndex === index ? "true" : "false"}
        @mouseenter=${() => (this.activeIndex = index)}
        @click=${() => this._trackClick(hit, index)}
      >
        ${hit.image_url
          ? html`<div class="scgb-result__thumb">
              <img src=${hit.image_url} alt=${hit.image_alt ?? ""} loading="lazy" />
            </div>`
          : nothing}
        <div>
          <div class="scgb-result__meta">${label}</div>
          <h3 class="scgb-result__title">${unsafeHTML(titleHtml)}</h3>
          ${this._renderSubline(hit, excerptHtml)}
        </div>
      </a>
    `;
  }

  private _renderSubline(hit: SearchDoc, excerptHtml: string): TemplateResult {
    // Per-post-type subline mirroring the variants in the legacy search.php.
    switch (hit.post_type) {
      case "sc_resort":
      case "holiday":
        return html`<p class="scgb-result__sub">
          ${hit.attrs.country ?? ""}${hit.attrs.country && hit.attrs.price_text ? " · " : ""}
          ${hit.attrs.price_text ?? ""}
        </p>`;
      case "sc_event":
        return html`<p class="scgb-result__sub">
          ${hit.attrs.event_date_text ?? ""}
        </p>`;
      case "ski-test":
        return html`<p class="scgb-result__sub">
          ${hit.attrs.price_text ? `Price: ${hit.attrs.price_text}` : ""}
          ${hit.attrs.ski_ability ? ` · Ability: ${hit.attrs.ski_ability}` : ""}
        </p>`;
      case "product":
        return html`<p class="scgb-result__sub">${hit.attrs.price_text ?? ""}</p>`;
      case "sc_discount":
      case "affiliates_discount":
        return html`<p class="scgb-result__sub">${unsafeHTML(excerptHtml)}</p>`;
      case "post":
      case "sc_community_post":
        return html`<p class="scgb-result__sub">
          ${hit.date_published
            ? new Date(hit.date_published * 1000).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "2-digit",
              })
            : ""}
        </p>`;
      default:
        return html`<p class="scgb-result__sub">${unsafeHTML(excerptHtml)}</p>`;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "scgb-search": ScgbSearchElement;
  }
}
